import React, {ReactNode, useState} from 'react';
import {
    Avatar,
    Breadcrumb,
    ConfigProvider,
    Drawer,
    Dropdown,
    Flex,
    Layout as AntdLayout,
    Menu,
    MenuProps,
} from 'antd';
import {
    DollarOutlined,
    LogoutOutlined,
    MenuFoldOutlined,
    MenuUnfoldOutlined,
    UserOutlined
} from '@ant-design/icons';
import AuthAction from "../../redux/actions/authActions/Actions";
import {useDispatch, useSelector} from "react-redux";
import {User} from "../../models/User";
import {Link} from "react-router-dom";
import {AuthService} from "../../services/AuthService";
import LoadingScreen from "../LoadingScreen/LoadingScreen";
import {menuConfig} from "../../config/MenuConfig";
import {ItemType} from "antd/es/breadcrumb/Breadcrumb";
import esES from 'antd/locale/es_ES';
import Title from "antd/es/typography/Title";
import { useMediaQuery } from 'react-responsive';
import { MenuStore } from '../../redux/reducers/MenuReducer';
import { useThemeLayoutConfig } from '../../config/ThemeDashboardLayoutConfig';
import MenuAction from '../../redux/actions/menuActions/Action';

import './Layout.scss';

interface LayoutProps {
    children: ReactNode;
    loading?: boolean;
    title?: string;
    breadcrumb?: ItemType[];
}

const { Header, Content, Sider } = AntdLayout;

function Layout({ children, loading = false, title = '', breadcrumb = [] }: LayoutProps) {
   const themeConfig = useThemeLayoutConfig();

    const dispatch = useDispatch();
    const authUser: User = useSelector((state: any) => state.auth);
    const { selectedKeys, openKeys }: MenuStore = useSelector((state: any) => state.menu);
    const [localLoading, setLocalLoading] = useState(false);
    const isDesktop = useMediaQuery({ query: '(min-width: 768px)' });

    const [isToggled, setToggled] = useState(false);
    const toggleTrueFalse = () => setToggled(!isToggled);
    const onClose = () => {
        setToggled(false);
    };

    const logout = async () => {
        setLocalLoading(true);

        if (authUser.token) {
            await AuthService.logout();
        }

        dispatch(AuthAction.signOut({}));
        setLocalLoading(false);
    }

    const userInitials = `${authUser.name?.slice(0, 1) || ''}${authUser.lastname?.slice(0, 1) || ''}`.toUpperCase();
    const userFullName = `${authUser.name || ''} ${authUser.lastname || ''}`.trim();

    const items: MenuProps['items'] = [
        {
            label: <a onClick={() => { console.log("Perfil"); }}><UserOutlined style={{ marginRight: '7.5px' }}/> Perfil</a>,
            key: '1',
        },
        {
            label: <a onClick={() => { console.log("Suscripción"); }}><DollarOutlined style={{ marginRight: '7.5px' }} /> Suscripción</a>,
            key: '2',
        },
        {
            type: 'divider'
        },
        {
            label: <a onClick={() => { logout(); }}><LogoutOutlined style={{ marginRight: '7.5px' }} /> Cerrar sesión</a>,
            key: '3',
        },
    ];

    const renderMenu = (onNavigate?: () => void) => (
        <Menu
            className="app-sider-menu"
            mode="inline"
            selectedKeys={selectedKeys}
            openKeys={openKeys}
            forceSubMenuRender
            onOpenChange={(openKeysUpdated) => {
                dispatch(MenuAction.update({ openKeys: openKeysUpdated }));
            }}
            onClick={() => {
                onNavigate?.();
            }}
        >
            {
                menuConfig(authUser).menus.map((menu) => (
                    menu.children ? (
                        <Menu.SubMenu title={menu.label} key={menu.key} icon={menu.icon} disabled={menu.disabled}>
                            {
                                menu.children.map((submenuItem: any) => (
                                    <Menu.Item key={submenuItem.key} icon={submenuItem.icon} disabled={submenuItem.disabled}>
                                        {
                                            submenuItem.path && !submenuItem.disabled
                                                ? (<Link to={submenuItem.path}>{submenuItem.label}</Link>)
                                                : <span>{submenuItem.label}</span>
                                        }
                                    </Menu.Item>
                                ))
                            }
                        </Menu.SubMenu>
                    ) : (
                        <Menu.Item key={menu.key} icon={menu.icon} disabled={menu.disabled}>
                            {
                                menu.path && !menu.disabled
                                    ? (<Link to={menu.path}>{menu.label}</Link>)
                                    : <span>{menu.label}</span>
                            }
                        </Menu.Item>
                    )
                ))
            }
        </Menu>
    );

    return (
        <ConfigProvider
            theme={themeConfig}
            locale={esES}
        >
            <LoadingScreen loading={(loading || localLoading)}/>
            <AntdLayout className="app-shell">
                {
                    !isDesktop && (
                        <Drawer
                            className="app-mobile-drawer"
                            placement="left"
                            onClose={onClose}
                            closable={false}
                            open={isToggled}
                            bodyStyle={{ padding: 0 }}
                            width={300}
                        >
                            <div className="app-sider-brand">
                                <img
                                    className="app-sider-brand-logo"
                                    src="/logo_touchlatam_white.png"
                                    alt="Touchlatam"
                                />
                            </div>
                            <div className="app-sider-label">Navegación</div>
                            {renderMenu(onClose)}
                        </Drawer>
                    )
                }

                <Header className="app-header">
                    <div className="app-header-left">
                        {!isDesktop && (
                            <button
                                type="button"
                                className="app-header-menu-trigger"
                                onClick={toggleTrueFalse}
                                aria-label="Abrir menú"
                            >
                                {isToggled ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                            </button>
                        )}

                        <div className="app-header-brand">
                            <img
                                className="app-header-logo"
                                src="/logo_touchlatam_white.png"
                                alt="Touchlatam"
                            />
                            <span className="app-header-divider" />
                            <span className="app-header-product">Solimain</span>
                        </div>
                    </div>

                    <Dropdown
                        menu={{ items }}
                        trigger={["click"]}
                        placement="bottomRight"
                        arrow
                    >
                        <button type="button" className="app-header-user">
                            <Avatar className="app-header-avatar" size={36}>
                                {userInitials}
                            </Avatar>
                            {isDesktop && (
                                <span className="app-header-user-meta">
                                    <span className="app-header-user-name">{userFullName || authUser.username}</span>
                                    <span className="app-header-user-email">{authUser.email}</span>
                                </span>
                            )}
                        </button>
                    </Dropdown>
                </Header>

                <AntdLayout className="app-body">
                    {
                        isDesktop && (
                            <Sider
                                className="app-sider"
                                width={268}
                                collapsedWidth={0}
                                collapsed={false}
                            >
                                <div className="app-sider-label">Navegación</div>
                                {renderMenu()}
                            </Sider>
                        )
                    }
                    <AntdLayout className="app-content-shell">
                        <Content className="app-content">
                            {
                                (breadcrumb.length > 0 || title) && (
                                    <div className="app-page-header">
                                        {
                                            breadcrumb.length > 0 && (
                                                <Breadcrumb
                                                    className="app-page-breadcrumb"
                                                    separator="/"
                                                    items={breadcrumb}
                                                />
                                            )
                                        }
                                        {
                                            title && (
                                                <Title level={3} className="app-page-title">{title}</Title>
                                            )
                                        }
                                    </div>
                                )
                            }
                            <div className="app-page-body">
                                {children}
                            </div>
                        </Content>
                    </AntdLayout>
                </AntdLayout>
            </AntdLayout>
        </ConfigProvider>
    );
}

export default Layout;
